/**
 * GST Utilities for QuickBill Frontend
 * 
 * Handles state mappings and GST calculations
 */

// Indian states with their GST state codes
export const indianStates = [
  { code: 'AP', name: 'Andhra Pradesh', gstCode: '28' },
  { code: 'AR', name: 'Arunachal Pradesh', gstCode: '12' },
  { code: 'AS', name: 'Assam', gstCode: '18' },
  { code: 'BR', name: 'Bihar', gstCode: '10' },
  { code: 'CT', name: 'Chhattisgarh', gstCode: '22' },
  { code: 'GA', name: 'Goa', gstCode: '30' },
  { code: 'GJ', name: 'Gujarat', gstCode: '24' },
  { code: 'HR', name: 'Haryana', gstCode: '06' },
  { code: 'HP', name: 'Himachal Pradesh', gstCode: '02' },
  { code: 'JH', name: 'Jharkhand', gstCode: '20' },
  { code: 'KA', name: 'Karnataka', gstCode: '29' },
  { code: 'KL', name: 'Kerala', gstCode: '32' },
  { code: 'MP', name: 'Madhya Pradesh', gstCode: '23' },
  { code: 'MH', name: 'Maharashtra', gstCode: '27' },
  { code: 'MN', name: 'Manipur', gstCode: '14' },
  { code: 'ML', name: 'Meghalaya', gstCode: '17' },
  { code: 'MZ', name: 'Mizoram', gstCode: '15' },
  { code: 'NL', name: 'Nagaland', gstCode: '13' },
  { code: 'OR', name: 'Odisha', gstCode: '21' },
  { code: 'PB', name: 'Punjab', gstCode: '03' },
  { code: 'RJ', name: 'Rajasthan', gstCode: '08' },
  { code: 'SK', name: 'Sikkim', gstCode: '11' },
  { code: 'TN', name: 'Tamil Nadu', gstCode: '33' },
  { code: 'TG', name: 'Telangana', gstCode: '36' },
  { code: 'TR', name: 'Tripura', gstCode: '16' },
  { code: 'UP', name: 'Uttar Pradesh', gstCode: '09' },
  { code: 'UT', name: 'Uttarakhand', gstCode: '05' },
  { code: 'WB', name: 'West Bengal', gstCode: '19' },
  { code: 'AN', name: 'Andaman and Nicobar Islands', gstCode: '35' },
  { code: 'CH', name: 'Chandigarh', gstCode: '04' },
  { code: 'DN', name: 'Dadra and Nagar Haveli', gstCode: '26' },
  { code: 'DD', name: 'Daman and Diu', gstCode: '25' },
  { code: 'DL', name: 'Delhi', gstCode: '07' },
  { code: 'JK', name: 'Jammu and Kashmir', gstCode: '01' },
  { code: 'LA', name: 'Ladakh', gstCode: '01' },
  { code: 'LD', name: 'Lakshadweep', gstCode: '31' },
  { code: 'PY', name: 'Puducherry', gstCode: '34' }
]

/**
 * Get GST state code from state name
 * @param {string} stateName - Full state name
 * @returns {string|null} GST state code or null if not found
 */
export function getGSTStateCode(stateName) {
  const state = indianStates.find(s => s.name === stateName)
  return state ? state.gstCode : null
}

/**
 * Determine if transaction is inter-state based on state names
 * @param {string} businessState - Business location state name
 * @param {string} customerState - Customer location state name
 * @returns {boolean} True if inter-state transaction
 */
export function isInterStateTransaction(businessState, customerState) {
  if (!businessState || !customerState) {
    return false // If no customer state, assume intra-state
  }
  
  const businessGSTCode = getGSTStateCode(businessState)
  const customerGSTCode = getGSTStateCode(customerState)
  
  return businessGSTCode !== customerGSTCode
}

/**
 * Calculate GST breakdown for a transaction item
 * @param {number} amount - Taxable amount (before GST)
 * @param {number} gstRate - Total GST rate (5, 12, 18, 28)
 * @param {boolean} isInterState - Whether it's inter-state transaction
 * @returns {object} GST breakdown with CGST, SGST, IGST amounts
 */
export function calculateItemGST(amount, gstRate, isInterState = false) {
  const totalGstAmount = (amount * gstRate) / 100
  
  if (isInterState) {
    // Inter-state: Only IGST
    return {
      cgstRate: 0,
      sgstRate: 0,
      igstRate: gstRate,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: parseFloat(totalGstAmount.toFixed(2)),
      totalGst: parseFloat(totalGstAmount.toFixed(2)),
      taxType: 'IGST'
    }
  } else {
    // Intra-state: CGST + SGST (split equally)
    const cgstAmount = totalGstAmount / 2
    const sgstAmount = totalGstAmount / 2
    
    return {
      cgstRate: gstRate / 2,
      sgstRate: gstRate / 2,
      igstRate: 0,
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      igstAmount: 0,
      totalGst: parseFloat(totalGstAmount.toFixed(2)),
      taxType: 'CGST+SGST'
    }
  }
}

/**
 * Calculate total GST summary for a cart/transaction
 * @param {Array} items - Array of cart items with GST calculations
 * @param {boolean} isInterState - Whether transaction is inter-state
 * @returns {object} GST summary
 */
export function calculateGSTSummary(items, isInterState) {
  const summary = {
    cgstTotal: 0,
    sgstTotal: 0,
    igstTotal: 0,
    totalGst: 0,
    isInterState,
    taxType: isInterState ? 'IGST' : 'CGST+SGST'
  }
  
  items.forEach(item => {
    if (item.gstDetails) {
      summary.cgstTotal += item.gstDetails.cgstAmount || 0
      summary.sgstTotal += item.gstDetails.sgstAmount || 0
      summary.igstTotal += item.gstDetails.igstAmount || 0
      summary.totalGst += item.gstDetails.totalGst || 0
    }
  })
  
  // Round to 2 decimal places
  summary.cgstTotal = parseFloat(summary.cgstTotal.toFixed(2))
  summary.sgstTotal = parseFloat(summary.sgstTotal.toFixed(2))
  summary.igstTotal = parseFloat(summary.igstTotal.toFixed(2))
  summary.totalGst = parseFloat(summary.totalGst.toFixed(2))
  
  return summary
}

/**
 * Extract GST from an inclusive amount (GST-inclusive pricing)
 * @param {number} inclusiveAmount - Amount that includes GST
 * @param {number} gstRate - Total GST rate (5, 12, 18, 28)
 * @param {boolean} isInterState - Whether transaction is inter-state
 * @returns {object} GST breakdown with base amount and tax components
 */
export function extractGSTFromInclusiveAmount(inclusiveAmount, gstRate, isInterState = false) {
  // If GST rate is 0, return the full amount as base amount
  if (!gstRate || gstRate === 0) {
    return {
      baseAmount: parseFloat(inclusiveAmount.toFixed(2)),
      cgstRate: 0,
      sgstRate: 0,
      igstRate: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalGst: 0,
      taxType: 'None'
    }
  }
  
  // Calculate base amount (exclusive of GST)
  const baseAmount = inclusiveAmount / (1 + (gstRate / 100))
  const totalGstAmount = inclusiveAmount - baseAmount
  
  if (isInterState) {
    // Inter-state: Only IGST
    return {
      baseAmount: parseFloat(baseAmount.toFixed(2)),
      cgstRate: 0,
      sgstRate: 0,
      igstRate: gstRate,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: parseFloat(totalGstAmount.toFixed(2)),
      totalGst: parseFloat(totalGstAmount.toFixed(2)),
      taxType: 'IGST'
    }
  } else {
    // Intra-state: CGST + SGST (split equally)
    const cgstAmount = totalGstAmount / 2
    const sgstAmount = totalGstAmount / 2
    
    return {
      baseAmount: parseFloat(baseAmount.toFixed(2)),
      cgstRate: gstRate / 2,
      sgstRate: gstRate / 2,
      igstRate: 0,
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      igstAmount: 0,
      totalGst: parseFloat(totalGstAmount.toFixed(2)),
      taxType: 'CGST+SGST'
    }
  }
}
